import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { rem } from 'polished'
import clsx from 'clsx'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { getHasLuxPlusOrSubscriptionInCart } from 'checkout/selectors/view/luxPlusSubscription'
import { connect } from 'react-redux'
import styled from 'styled-components'
import Image from 'components/Common/Image'
import { EXP_CATEGORY_AIRPORT_LOUNGE_ID, EXP_CATEGORY_AIRPORT_TRANSFER_ID, EXP_CATEGORY_WATER_TRANSFER_ID, EXP_CATEGORY_PRIVATE_TRANSFER_ID, EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW, EXP_CATEGORY_PHOTOSHOOT_TRAVELSHOOT_ID } from 'constants/experience'
import NestableClickable from 'components/Common/NestableClickable'
import ExperienceCancellationText from 'components/Experiences/ExperienceCancellationPolicy/ExperienceCancellationText'
import ExperienceGiveAsGiftInfoText from 'components/Experiences/ExperienceGiveAsGiftModal/ExperienceGiveAsGiftInfoText'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import config from 'constants/config'
import Typography from 'LuxKit/Typography/Typography'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { addQueryParamsToPath } from 'lib/url/searchUrlUtils'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useInView } from 'react-intersection-observer'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import ExperienceAppDiscountTag from '../ExperienceAppDiscountTag'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import ExperienceCategoryLabel from './ExperienceCategoryLabel'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import ExperienceTilePrices from './ExperienceTilePrices'
import NumberRating from 'components/Common/NumberRating'
import { isExperienceOfferRatingDisplayable } from 'lib/order/reviewUtils'
import AgentHubAdditionalCommissionLabel from 'agentHub/components/AgentHubAdditionalCommissionLabel'
import ContentTagLabels from 'components/Content/ContentTagLabels'
import { getAccommodationTagIds } from 'checkout/selectors/experienceSelectors'
import ExperienceUrgencyTag from './ExperienceUrgencyTag'

const ExperienceImageContainer = styled.div`
  position: relative;
  height: ${rem(162)};
  width: 100%;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const BadgeContainer = styled.div`
  position: absolute;
  top: ${rem(8)};
  left: ${rem(8)};
  right: ${rem(8)};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${rem(2)};
`

const Tile = styled(NestableClickable)`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    min-height: ${rem(364)};
  }

  &.tag-assigned {
    border: 2px solid ${props => props.theme.palette.highlight.primary.darkBackground};
  }
`

const TileContent = styled.div`
  position: relative;
  padding: ${rem(20)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;

  > * + * {
    margin-top: ${rem(8)};
  }
`

const RightSideBadge = styled.div`
  position: absolute;
  top: ${rem(12)};
  right: ${rem(12)};
`

const ExperienceAddedBadge = styled(Group)`
  background-color: ${props => props.theme.palette.messaging.success.lightBackground};
  padding: ${rem(4)};
`

const UrgencyBadgeContainer = styled.div`
  position: absolute;
  bottom: ${rem(8)};
  left: ${rem(8)};
  z-index: 1;
`

const TRANSFER_AND_LOUNGE_CATEGORIES = [
  EXP_CATEGORY_AIRPORT_TRANSFER_ID,
  EXP_CATEGORY_AIRPORT_LOUNGE_ID,
  EXP_CATEGORY_WATER_TRANSFER_ID,
  EXP_CATEGORY_PRIVATE_TRANSFER_ID,
  EXP_CATEGORY_PHOTOSHOOT_TRAVELSHOOT_ID,
]

interface Props {
  experience: App.ExperienceBookingView
  partnershipBadge?: boolean
  onViewDetails?: (experience: App.ExperienceBookingView) => void
  to?: string
  isStandaloneExperience?: boolean
  className?: string
  position?: number
  isLuxPlusEnabled?: boolean
  hasLuxPlusOrSubscriptionInCart?: boolean
  tagIds?: Array<string>
  listKey?: string
  offerMetadata?: App.ExperienceOffersMetadata[string]
}

function ExperienceTile(props: Props) {
  const {
    className,
    experience,
    onViewDetails,
    to,
    isStandaloneExperience = true,
    isLuxPlusEnabled = false,
    hasLuxPlusOrSubscriptionInCart = false,
    position,
    tagIds,
    listKey,
    offerMetadata: offerMetadataProp,
  } = props

  const offerMetadataFromState = useAppSelector(state => listKey ? state.experience.experienceOffersMetadata[listKey]?.[experience.id] : undefined)
  const offerMetadata = offerMetadataProp ?? offerMetadataFromState

  const intl = useIntl()
  const trackEvent = useTrackExperienceOfferEvent()
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: OFFER_TRACKING_IN_VIEW_THRESHOLD,
  })

  const originalExperience = experience.originalExperience

  const onViewDetailsClick = useCallback(() => {
    if (position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: originalExperience,
        position,
        key: EventDataKey.ProductClick,
      })
    }

    trackEvent('OFFER_TILE_CLICKED', [{
      type: 'click',
      data: {
        offer: originalExperience,
      },
    }])
    onViewDetails?.(experience)
  }, [experience, originalExperience, dispatchOfferListEvent, onViewDetails, position, trackEvent])

  useEffect(() => {
    if (inView && imageLoaded && position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer: originalExperience,
        position,
        key: EventDataKey.ProductImpression,
      })

      trackEvent('OFFER_TILE_IMPRESSION', [{
        type: 'impression',
        data: {
          offer: originalExperience,
        },
      }])
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- Only
  }, [inView, imageLoaded])

  const tripId = useAppSelector(getImmersiveTripId)
  const linkParams = useMemo(() => {
    const updatedTo = (to && config.OPEN_NEW_TAB_OFFER_CLICK && tripId) ?
        addQueryParamsToPath(to, { tripId }) :
      to

    return {
      onClick: onViewDetailsClick,
      to: updatedTo,
      ...(config.OPEN_NEW_TAB_OFFER_CLICK ? { target: '_blank' } : {}),
    }
  }, [tripId, to, onViewDetailsClick])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorExperienceOptions(originalExperience, {
      memberPrice: isLuxPlusEnabled && hasLuxPlusOrSubscriptionInCart ? originalExperience.pricing.memberPrice : 0,
    }),
  ], [originalExperience, isLuxPlusEnabled, hasLuxPlusOrSubscriptionInCart])

  const isTransferOrLounge = experience.categories.some(category =>
    TRANSFER_AND_LOUNGE_CATEGORIES.includes(category.id),
  )

  const showDistanceText = useMemo(() => {
    return experience.distance && !isTransferOrLounge
  }, [experience.distance, isTransferOrLounge])

  const showLuxPlusMemberPrice = useMemo(() => {
    // show for standalone experiences if feature flag is enabled
    if (isStandaloneExperience && isLuxPlusEnabled && experience.pricing.memberPrice > 0) return true

    // show on in flow if user has lux plus or subscription in cart
    if (!isStandaloneExperience && isLuxPlusEnabled && hasLuxPlusOrSubscriptionInCart && experience.pricing.memberPrice > 0) return true

    return false
  }, [isStandaloneExperience, isLuxPlusEnabled, hasLuxPlusOrSubscriptionInCart, experience.pricing.memberPrice])

  const showDiscount = experience.pricing.discountPercent >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW && (isStandaloneExperience || !showLuxPlusMemberPrice)

  const { totalMemberPrice, totalSalePrice, totalGrossPrice, memberDiscountPercentage } = useMemo(() => {
    const totalMemberPrice = experience.cartData ? experience.pricing.memberPrice * experience.cartData.count : experience.pricing.memberPrice
    const totalSalePrice = experience.cartData?.totalPrice ?? experience.price
    const totalGrossPrice = experience.cartData ? (experience.cartData?.totalGrossPrice) : experience.pricing.value

    return {
      totalMemberPrice: (isStandaloneExperience || hasLuxPlusOrSubscriptionInCart) ? totalMemberPrice : undefined,
      totalSalePrice: (isStandaloneExperience || !hasLuxPlusOrSubscriptionInCart || !totalMemberPrice) ? totalSalePrice : undefined,
      totalGrossPrice: (!isStandaloneExperience && hasLuxPlusOrSubscriptionInCart) ? totalGrossPrice : undefined,
      memberDiscountPercentage: (!experience.cartData?.count) ? experience.pricing.memberDiscountPercentage : undefined,
    }
  }, [experience, isStandaloneExperience, hasLuxPlusOrSubscriptionInCart])

  const hasTagAssigned = useMemo(() => {
    return tagIds?.some(tagId => experience.tagIds?.includes(tagId))
  }, [tagIds, experience.tagIds])

  const editLabel = intl.formatMessage({
    id: '5agUUZ5osThBLWohVwti',
    defaultMessage: 'Edit',
    description: 'Button text to edit an experience already added to cart',
  })
  const viewLabel = intl.formatMessage({
    id: 'IZisqxEC06wyEPpzbzQt',
    defaultMessage: 'View',
    description: 'Button text to view experience offer details for the experience',
  })

  return (
    <Tile
      {...linkParams}
      as={to ? ClickableLink : undefined}
      data-testid="experience-tile"
      className={clsx(className, { 'tag-assigned': hasTagAssigned })}
    >
      <ExperienceImageContainer>
        {experience.image && <Image
          height={126}
          image={experience.image}
          fit="center"
          dpr={2}
          onLoad={() => setImageLoaded(true)}
        />}
        <BadgeContainer>
          <div>
            <LabelGroup>
              {hasTagAssigned && <ContentTagLabels offerId={experience.originalExperience.id} page="search" />}
              {!hasTagAssigned && <ExperienceCategoryLabel categories={experience.categories} />}
            </LabelGroup>
          </div>
          <TripGuard>
            <ExperienceBookmarkButton offer={originalExperience} withLabel="never" size="small" />
          </TripGuard>
        </BadgeContainer>
        {config.EXPERIENCE_BADGE_IMAGE_ID && <RightSideBadge>
          <Image id={config.EXPERIENCE_BADGE_IMAGE_ID} height={80} width={80} />
        </RightSideBadge>}
        {offerMetadata?.urgencyTags && offerMetadata.urgencyTags.length > 0 && (
          <UrgencyBadgeContainer>
            <ExperienceUrgencyTag urgencyTags={offerMetadata.urgencyTags} />
          </UrgencyBadgeContainer>
        )}
      </ExperienceImageContainer>
      {!!experience.cartData && <ExperienceAddedBadge direction="horizontal" gap={2} horizontalAlign="center">
        <LineCheckCircleIcon size="XS" />
        <CaptionTextBlock variant="medium" weight="normal" colour="light-foreground-alt">
          {intl.formatMessage(
            {
              id: 'BU0X5V7wGeD0B1aq6Cuc',
              defaultMessage: '{count}x added',
              description: 'Label showing how many times an experience has been added to cart',
            },
            { count: experience.cartData.count },
          )}
        </CaptionTextBlock>
      </ExperienceAddedBadge>}
      <TileContent ref={inViewRef}>
        <div>
          <VerticalSpacer gap={8}>
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <ExperienceAppDiscountTag experienceOffer={originalExperience} />
              <AgentHubAdditionalCommissionLabel offerId={originalExperience?.id} />
            </Group>
            <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between" gap={4}>
              <VerticalSpacer gap={2}>
                <Typography wrap="pretty">
                  {experience.compactLocation && <Caption as="span" variant="large" weight="normal">{experience.compactLocation}</Caption>}
                </Typography>
                {showDistanceText && <Typography wrap="pretty">
                  <Caption as="span" variant="large" colour="light-foreground-alt" weight="normal">
                    <FormattedMessage
                      id="C_2Le5x9iSfeQHNV2_vv"
                      defaultMessage="{distance} km from hotel"
                      description="Distance from hotel for an experience, e.g. '2.5 km from hotel'"
                      values={{
                        distance: <FormatDecimal maximumFractionDigits={experience.distance && experience.distance > 1 ? 0 : 1} value={experience.distance ?? 0} />,
                      }}
                    />
                  </Caption>
                </Typography>}
              </VerticalSpacer>
              {config.EXPERIENCES_REVIEWS_ENABLED && isExperienceOfferRatingDisplayable(experience) && <NumberRating
                rating={experience.rating?.score}
                total={experience.rating?.reviewsTotal}
                variant="xs"
                numberPosition="start"
                onlyNumber
              />}
            </Group>
            <BodyText title={experience.title} lineClamp={3} variant="medium" weight="bold">{experience.title}</BodyText>
            {originalExperience?.cancellationPolicies.length > 0 && isStandaloneExperience && <ExperienceCancellationText
              cancellationInfo={originalExperience?.copy.cancellationInfo}
            />}
            {!!originalExperience.giftType && isStandaloneExperience && <ExperienceGiveAsGiftInfoText experience={originalExperience} />}
            <LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" hideStatusCredits />
          </VerticalSpacer>
        </div>
        <VerticalSpacer gap={4}>
          {showDiscount && <PriceRowValueDiscount
            type="estimate"
            discountPercentage={experience.pricing.discountPercent}
          />}
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={4}>
            <div>
              <ExperienceTilePrices
                totalPrice={totalSalePrice}
                totalMemberPrice={totalMemberPrice}
                totalGrossPrice={totalGrossPrice}
                isFromPrice={!experience.cartData}
                ticketUnitLabel={experience.ticketUnitLabel ?? 'person'}
                discountMemberPercentage={memberDiscountPercentage}
              />
              <PriceRowAgentHubCommission
                offerId={experience.id}
                offer={experience.originalExperience}
                productType="experience"
                offerLocations={experience.location ? [experience.location] : undefined}
              />
            </div>
            <TextButton kind="secondary" nonInteractable startIcon={experience.cartData ? <LineEditAltIcon /> : undefined}>
              {experience.cartData ? editLabel : viewLabel}
            </TextButton>
          </Group>
        </VerticalSpacer>
      </TileContent>
    </Tile>
  )
}

const mapStateToProps = (state: App.State) => {
  return {
    tagIds: getAccommodationTagIds(state),
    isLuxPlusEnabled: getIsEnabledLuxPlusDiscountedExperiences(state),
    hasLuxPlusOrSubscriptionInCart: getHasLuxPlusOrSubscriptionInCart(state),
  }
}

export default connect(mapStateToProps)(ExperienceTile)
